"use client";

import { Icon } from "@templates/design-system";
import { useState } from "react";
import { DaylightMedia } from "../DaylightMedia/DaylightMedia";
import type { IVideoPlayerProps } from "./VideoPlayer.props";

export const VideoPlayer = ({
  banner,
  directVideo,
  source,
  vimeoVideo,
  youtubeVideo,
}: IVideoPlayerProps) => {
  const video =
    source === "direct" ? directVideo : source === "youtube" ? youtubeVideo : vimeoVideo;
  const [playing, setPlaying] = useState(false);
  if (!video) return null;
  const autoplay = `${video}${video.includes("?") ? "&" : "?"}autoplay=1`;
  const player =
    source === "direct" ? (
      <video
        autoPlay={playing}
        className="absolute inset-0 h-full w-full bg-background object-cover"
        controls
        playsInline
        src={video}
      />
    ) : (
      <iframe
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 h-full w-full"
        loading="lazy"
        src={playing ? autoplay : video}
        title={`${source} video`}
      />
    );

  return (
    <div className="relative aspect-video overflow-hidden rounded-[14px] bg-background">
      {banner && !playing ? (
        <button
          aria-label="Play video"
          className="group absolute inset-0 block h-full w-full cursor-pointer overflow-hidden text-left focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
          onClick={() => setPlaying(true)}
          type="button"
        >
          {banner ? (
            <>
              <DaylightMedia
                aspectRatio="16:9"
                className="pointer-events-none h-full rounded-none"
                imageClassName="object-cover transition-transform duration-300 ease-out group-hover:scale-[1.015]"
                media={banner.image}
                sizes="(min-width: 1280px) 1184px, 100vw"
              />
              <span aria-hidden="true" className="absolute inset-0 bg-void/45" />
            </>
          ) : (
            <span className="absolute inset-0 flex items-end bg-sky p-7 text-ink md:p-10">
              <span
                aria-hidden="true"
                className="absolute -right-12 -top-16 size-64 rounded-full bg-lilac transition-transform duration-300 ease-out group-hover:scale-105"
              />
              <span
                aria-hidden="true"
                className="absolute right-10 top-10 size-24 rounded-full border border-cobalt/35"
              />
              <span className="relative max-w-md">
                <span className="block font-daylight-body text-sm font-semibold text-cobalt">
                  Video · {source}
                </span>
                <span className="mt-3 block font-daylight-display text-3xl font-semibold leading-tight md:text-5xl">
                  Press play when you are ready.
                </span>
              </span>
            </span>
          )}
          <span className="absolute left-1/2 top-1/2 inline-flex size-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-primary bg-primary text-primary-foreground transition-[transform,background-color] duration-200 group-hover:scale-105 group-hover:bg-signal-dim group-active:scale-95">
            <Icon className="ml-1 size-6" name="play" />
          </span>
        </button>
      ) : (
        player
      )}
    </div>
  );
};

VideoPlayer.displayName = "Content::VideoPlayer";
